import { useMemo, useState } from "react";
import { GoAiguaSelectOption } from "types/generic";

type GoAiguaSelectProps = {
  selectedOption: string;
  setSelectedOption: (optionValue: string) => void;
  selectedValue?: string;
  setSelectedValue?: (selectedValue: string) => void ;
  options: GoAiguaSelectOption[];
  placeholder?: string;
};

const GoAiguaSelect = ({
  selectedOption,
  setSelectedOption,
  selectedValue,
  setSelectedValue,
  options,
  placeholder = "Select an option...",
}: GoAiguaSelectProps) => {
  const [isBoxFocused, setIsBoxFocused] = useState(false);
  const [isHeadFocused, setIsHeadFocused] = useState(false);

  /*    
    Event handles for box focusing
    relying on that events will trigger
    before 50 ms. Kind of dirty, but it works
    just fine...
*/

  const boxEventHandlers = useMemo(
    () => ({
      onFocus: () => setIsBoxFocused(true),
      onBlur: () => {
        setTimeout(() => setIsBoxFocused(false), 50);
      },
    }),
    []
  );

  const headEventHandlers = useMemo(
    () => ({
      onFocus: () => setIsHeadFocused(true),
      onBlur: () => {
        setTimeout(() => setIsHeadFocused(false), 50);
      },
    }),
    []
  );

  const isBoxShown = () => {
    return isBoxFocused || isHeadFocused;
  };

  const generateOptionBoxClass = () => {
    if (isBoxShown() /* ? */) {
      return "c-goaigua-select__option-box";
    } else {
      return "c-goaigua-select__option-box --hidden";
    }
  };

  const displayTextOnHead = () => {
    if (selectedOption && selectedOption !== "") {
      for (let index in options) {
        if (options[index].value === selectedOption) {
          return (
            <div className="c-goaigua-select__text" key={options[index].label}>
              {options[index].label}
            </div>
          );
        }
      }
      return null;
    } else {
      return (
        <div className="c-goaigua-select__text --placeholder">
          {placeholder}
        </div>
      );
    }
  };

  const generateElementsFromOptions = () => {
    let res = [];
    for (let index in options) {
      let baseClass = "c-goaigua-select__option";
      const currentOption = options[index];
      if (currentOption.value === selectedOption) {
        baseClass += " --active";
      }
      res.push(
        <div
          className={baseClass}
          onClick={() => {
            setSelectedOption(currentOption.value);
            setIsBoxFocused(false);
            setIsHeadFocused(false);
            setSelectedValue?.(currentOption.label)
          }}
          key={currentOption.label}
        >
          {currentOption.label}
        </div>
      );
    }
    return res;
  };

  return (
    <div className="c-goaigua-select">
      <div
        tabIndex={0}
        className={
          "c-goaigua-select__head" + (isBoxShown() ? " --focused" : "")
        }
        {...headEventHandlers}
      >
        {displayTextOnHead()}
      </div>

      <div
        tabIndex={0}
        className={generateOptionBoxClass()}
        {...boxEventHandlers}
      >
        <div className="c-goaigua-select__option-list">
          {generateElementsFromOptions()}
        </div>
      </div>
    </div>
  );
};

export default GoAiguaSelect;
